import { useEffect, useRef } from 'react';


interface TurnstileProps {
  siteKey?: string;
  onVerify: (token: string) => void;
  theme?: 'light' | 'dark' | 'auto';
  size?: 'normal' | 'compact';
  action?: string;
  cData?: string;
  responseField?: boolean;
  responseFieldName?: string;
  className?: string;
}

export const Turnstile: React.FC<TurnstileProps> = ({
  siteKey = process.env.CLOUDFLARE_TURNSTILE_SITE_KEY,
  onVerify,
  theme = 'auto',
  size = 'normal',
  action,
  cData,
  responseField = false,
  responseFieldName = 'cf-turnstile-response',
  className,
}: TurnstileProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string | null>(null);

  useEffect(() => {
    if (!window.turnstile) {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);

      script.onload = () => renderWidget();
    } else {
      renderWidget();
    }

    return () => {
      if (widgetId.current && window.turnstile) {
        window.turnstile.remove(widgetId.current);
      }
    }
  }, [siteKey]);

  const renderWidget = () => {
    if (!containerRef.current || !window.turnstile) return;

    if (widgetId.current) {
      window.turnstile.remove(widgetId.current);
      widgetId.current = null;
    }

    if (window.turnstile && window.turnstile.render) {
      widgetId.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        theme: theme,
        size: size,
        callback: (token: string) => {
          onVerify(token);
        },
        'expired-callback': () => {
          onVerify('');
        },
        'error-callback': () => {
          onVerify('');
        },
        action: action,
        cData: cData,
        response_field: responseField,
        response_field_name: responseFieldName,
      } as TurnstileOptions);
    } else {
      setTimeout(renderWidget, 100);
    }
  };

  return <div ref={containerRef} className={className} />;
};

interface TurnstileOptions {
  sitekey: string;
  theme?: 'light' | 'dark' | 'auto';
  size?: 'normal' | 'compact';
  callback: (token: string) => void;
  'expired-callback'?: () => void;
  'error-callback'?: () => void;
  action?: string;
  cData?: string;
  response_field?: boolean;
  response_field_name?: string;
}

declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement, options: TurnstileOptions) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

export default Turnstile;
